import { User, UserRole } from "services/users/model";
import Configuration from "config";
import { getAddressDescription } from "./maps";
import { memoizeAsyncFactory } from "utils/async";
import { injectScript } from "utils/injectScript";
import assert from "utils/assert";
import { logError } from "services/logging";
import {
  isUserAdmin,
  isUserCompanyManager,
  isUserContentAdmin,
  isUserStudent,
  isUserTeacher,
  isUserTeamManager,
} from "./users/utils";
import { getUserFullName } from "services/users/utils";
declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
  }
}

type IntercomClient = NonNullable<Window["Intercom"]>;

interface IntercomSettings {
  app_id: string;
  name?: string;
  email?: string;
  user_id?: string;
  roles?: string;
  saved_address?: string;
  native_language?: string;
  student_type?: string | null;
  self_study_type?: string;
  user_type?: UserType;
  company?: {
    id: string;
    name: string;
    team_name?: string;
  };
  [key: string]: any;
}

type UserType = UserRole | "content-manager";

type IntercomEvent =
  | "session-booked"
  | "session-cancelled"
  | "session-rescheduled"
  | "level-test-finished";

const intercomAppId = Configuration.INTERCOM_APP_ID;
if (Configuration.SHOW_INTERCOM) {
  assert(intercomAppId, "Intercom app id is required when Intercom is enabled");
}
const INTERCOM_APP_ID = intercomAppId ?? "";
const INTERCOM_SCRIPT_SRC = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;

export async function bootIntercom(user: User | null) {
  if (!Configuration.SHOW_INTERCOM) {
    return;
  }

  const intercom = await getIntercom();
  intercom("boot", buildIntercomSettings(user));
}

function buildIntercomSettings(user: User | null): IntercomSettings {
  if (!user) {
    return {
      app_id: INTERCOM_APP_ID,
    };
  }

  const intercomSettings: IntercomSettings = {
    app_id: INTERCOM_APP_ID,
    name: getUserFullName(user.profile),
    email: user.profile.email ?? undefined,
    user_id: user.id,
    roles: JSON.stringify(user.roles),
    saved_address: getAddressDescription(user.profile.address),
  };

  if (user.profile && user.profile.native_language) {
    intercomSettings.native_language = user.profile.native_language;
  }

  if (user.student) {
    intercomSettings.student_type = user.student.type;
    intercomSettings.self_study_type = user.student.has_pro_access
      ? "Self-study-PRO"
      : "Self-study-BASIC";
  }

  intercomSettings.user_type = getUserType(user);

  if (user.company) {
    intercomSettings.company = {
      id: user.company.id,
      name: user.company.name,
    };
    const team = user.company.teams[0];
    if (team) {
      intercomSettings.company.team_name = team.name;
    }
  }

  for (const key in user.features) {
    intercomSettings["feature_" + key] = user.features[key as keyof typeof user.features];
  }

  return intercomSettings;
}

const getUserType = (user: User): UserType | undefined => {
  if (isUserCompanyManager(user)) {
    return "company-manager";
  } else if (isUserTeamManager(user)) {
    return "team-manager";
  } else if (isUserStudent(user)) {
    return "student";
  } else if (isUserTeacher(user)) {
    return "instructor";
  } else if (isUserContentAdmin(user)) {
    return "content-manager";
  } else if (isUserAdmin(user)) {
    return "admin";
  }

  return undefined;
};

export async function shutdownIntercom() {
  if (!Configuration.SHOW_INTERCOM) {
    return;
  }

  const intercom = await getIntercom();
  intercom("shutdown");
}

export function trackIntercomEvent(
  eventName: IntercomEvent,
  metadata?: Record<string, any>
) {
  if (!Configuration.SHOW_INTERCOM) {
    return;
  }

  void getIntercom()
    .then(intercom => {
      intercom("trackEvent", eventName, metadata);
    })
    .catch(logError);
}

export function getConversationUrl(userId: string): string {
  return (
    "https://app.intercom.io/apps/" + INTERCOM_APP_ID + "/users/show?user_id=" + userId
  );
}

async function loadIntercomScript(): Promise<IntercomClient> {
  await injectScript(INTERCOM_SCRIPT_SRC);
  assert(window.Intercom, "Intercom loaded without exposing window.Intercom");
  return window.Intercom;
}

const getIntercom = memoizeAsyncFactory(loadIntercomScript);
